import React, { useState, useCallback, useRef } from "react";
import { StyleSheet, TouchableOpacity, View, ScrollView } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import { z } from "zod";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomAdminModal from "../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import NewCustomAxioActionModal from "../../settings/UsersTabContainer/NewCustomAxioActionModal";
import { PlusCircle } from "../../../components/svgIcons";
import { MinusCircle } from "@phosphor-icons/react/dist/csr/MinusCircle";
import { Cloud } from "../../../components/svgs/CustomCoursework";

import * as FileSystem from "expo-file-system";
import * as ExcelJS from "exceljs";
import { worksheetToJson } from "../../../utils/excelUtils";

import { Platform } from "react-native";

const modals = {
  inviteConfirmation: {
    title: "Success!",
    ariaLabel: "Students Invited Successfully Modal",
    warningText: "The invitations have been sent to the students.",
    actionButtonText: "OK",
  },
  inviteError: {
    title: "Error!",
    ariaLabel: "Student Invitation Error Modal",
    warningText: "An error occurred while sending the invitations. Please try again.",
    actionButtonText: "CLOSE",
  },
};

export enum InviteStudentModalState {
  CLOSED = "CLOSED",
  INVITE_CONFIRMATION = "INVITE_CONFIRMATION",
  INVITE_ERROR = "INVITE_ERROR",
}

interface Student {
  name: string;
  email: string;
}

const studentSchema = z.object({
  name: z.string().min(1, "Student name is required"),
  email: z.string().email("Invalid email address"),
});

const formSchema = z.object({
  students: z.array(studentSchema),
});

type FormData = {
  students: Student[];
};

interface InviteStudentModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (students: Student[]) => Promise<boolean>;
  isSubmitting: boolean;
}

const templateData: Student[] = [
  { name: "Alice Johnson", email: "alice.johnson@example.com" },
  { name: "Bob Rivera", email: "bob.rivera@example.org" },
];

const InviteStudentModalContainer = ({ visible, onClose, onSubmit, isSubmitting }: InviteStudentModalProps) => {
  const [activeTab, setActiveTab] = useState<"manual" | "upload">("manual");

  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState(InviteStudentModalState.CLOSED);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { showToast } = useToast();

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    watch,
    getValues,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      students: [],
    },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray<FormData>({
    control,
    name: "students",
  });

  // Watch all student fields to validate
  const students = watch("students");
  const isLastStudentValid =
    students &&
    students.length > 0 &&
    students[students.length - 1]?.name?.trim() &&
    students[students.length - 1]?.email?.trim() &&
    !errors.students?.[students.length - 1];

  // Add initial student if array is empty
  React.useEffect(() => {
    if (fields.length === 0) {
      append({ name: "", email: "" } as Student);
    }
  }, [append, fields.length]);

  // Check if there are any errors in the students array
  const hasErrors = Object.keys(errors).length > 0;
  const isSubmitDisabled = hasErrors || students.length === 0 || !isValid;

  const handleParsedStudents = async (jsonData: Student[]) => {
    if (!jsonData.length) {
      showToast({
        message: "The uploaded file is empty or unreadable.",
        type: "error",
      });
      return;
    }

    const firstRow = jsonData[0];
    const requiredColumns = Object.keys(templateData[0]);

    const hasRequiredColumns = requiredColumns.every((col) => Object.keys(firstRow).includes(col));

    if (!hasRequiredColumns) {
      showToast({
        message: `The Excel file must include columns: ${requiredColumns.join(", ")} (case-sensitive).`,
        type: "error",
      });
      return;
    }
    const extractedStudents = jsonData
      .filter((row) => row.name && row.email)
      .map((row) => ({
        name: row.name.trim(),
        email: row.email.trim(),
      }));

    if (extractedStudents.length > 0) {
      const currentStudents = getValues("students");
      currentStudents.forEach((student, index) => {
        if (!student.name?.trim() && !student.email?.trim()) {
          remove(index);
        }
      });

      extractedStudents.forEach((student) => append(student));
      setActiveTab("manual");
      await trigger();
    }
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files[0];
    try {
      if (file) {
        if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
          showToast({
            message: "Please upload a valid Excel file (.xlsx or .xls)",
            type: "error",
          });
          return;
        }

        const fileUri = URL.createObjectURL(file);
        let jsonData: Student[] = [];

        if (Platform.OS === "web") {
          const response = await fetch(fileUri);
          const arrayBuffer = await response.arrayBuffer();

          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(arrayBuffer);
          const worksheet = workbook.worksheets[0];
          jsonData = worksheetToJson<Student>(worksheet);

          await handleParsedStudents(jsonData);
        } else {
          const fileData = await FileSystem.readAsStringAsync(fileUri, {
            encoding: FileSystem.EncodingType.Base64,
          });

          const buffer = Uint8Array.from(atob(fileData), (c) => c.charCodeAt(0));
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer.buffer);
          const worksheet = workbook.worksheets[0];
          jsonData = worksheetToJson<Student>(worksheet);

          await handleParsedStudents(jsonData);
        }
      }
    } catch (err) {
      console.error("File upload error:", err);
      showToast({
        message: "There was an error reading the file.",
        type: "error",
      });
    }
  };

  const handleFormSubmit = async (formData: FormData) => {
    try {
      // Filter out any empty or invalid entries
      const validStudents = formData.students.filter((student) => student.name?.trim() && student.email?.trim());

      const success = await onSubmit(validStudents);
      if (success) {
        reset({ students: [] });
        onClose();
        setModalState(InviteStudentModalState.INVITE_CONFIRMATION);
      } else {
        setModalState(InviteStudentModalState.INVITE_ERROR);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalState(InviteStudentModalState.INVITE_ERROR);
    }
  };

  const handleModalClose = () => {
    setModalState(InviteStudentModalState.CLOSED);
  };

  // Add helper function to get validation message
  const getValidationMessage = () => {
    if (students.length === 0) {
      return "Please add at least one student";
    }

    const emptyFields = students.some((student) => !student.name?.trim() || !student.email?.trim());
    if (emptyFields) {
      return "Please fill in all required fields";
    }

    if (hasErrors) {
      return "Please fix the validation errors above";
    }

    return "";
  };

  const onDownloadTemplate = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("InvitingUsers");
    worksheet.columns = [
      { header: "name", key: "name", width: 30 },
      { header: "email", key: "email", width: 30 },
    ];
    templateData.forEach((row) => worksheet.addRow(row));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inviting-user-template.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  return (
    <>
      <CustomAdminModal
        visible={visible}
        onRequestClose={onClose}
        title="Invite Students"
        ariaLabel="Invite students modal"
        style={{ width: isMedium ? "50%" : "90%" }}
        onShow={() => {}}>
        <View style={styles.container}>
          <View style={styles.tabSwitcher}>
            <TouchableOpacity
              onPress={() => setActiveTab("manual")}
              style={[styles.tabButton, activeTab === "manual" && styles.activeTabButton]}>
              <CustomText
                text="Manual Entry"
                useTranslationText={false}
                style={activeTab === "manual" ? styles.activeTabText : styles.inactiveTabText}
              />
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => setActiveTab("upload")}
              style={[styles.tabButton, activeTab === "upload" && styles.activeTabButton]}>
              <CustomText
                text="Upload Excel"
                useTranslationText={false}
                style={activeTab === "upload" ? styles.activeTabText : styles.inactiveTabText}
              />
            </TouchableOpacity>
          </View>
          {activeTab === "manual" ? (
            <ScrollView
              style={{ maxHeight: 400 }}
              contentContainerStyle={{ paddingBottom: 16 }}
              showsVerticalScrollIndicator={false}
              tabIndex={0}>
              {fields.map((field, index) => (
                <View key={field.id} style={styles.studentRow}>
                  <View style={styles.formGroup}>
                    <CustomText text="Student Name" size="s" style={styles.label} />
                    <View>
                      <Controller
                        control={control}
                        name={`students.${index}.name`}
                        render={({ field: { onChange, value } }) => (
                          <CustomTextInput
                            testID="student-name-input"
                            value={value}
                            onChangeText={onChange}
                            placeholder="John Doe"
                            error={errors.students?.[index]?.name?.message}
                            style={styles.input}
                          />
                        )}
                      />
                    </View>
                  </View>

                  <View style={styles.formGroup}>
                    <CustomText text="Email" size="s" style={styles.label} />
                    <View>
                      <Controller
                        control={control}
                        name={`students.${index}.email`}
                        render={({ field: { onChange, value } }) => (
                          <CustomTextInput
                            testID="student-email-input"
                            value={value}
                            onChangeText={onChange}
                            placeholder="john.doe@schoolname.org"
                            error={errors.students?.[index]?.email?.message}
                            style={styles.input}
                          />
                        )}
                      />
                    </View>
                  </View>

                  {fields.length > 1 && (
                    <CustomButton
                      text=""
                      onPress={() => remove(index)}
                      styleType="transparent"
                      size="m"
                      style={styles.removeButton}
                      // leftIcon={<PlusCircle color="#DC2626" />}
                      leftIcon={<MinusCircle color="#DC2626" size={24} />}
                      rightIcon={null}
                      disabled={false}
                      textProps={{}}
                      textStyle={{}}
                    />
                  )}
                </View>
              ))}

              <CustomButton
                text="Add Another Student"
                onPress={() => append({ name: "", email: "" } as Student)}
                styleType="secondary"
                size="m"
                style={styles.addButton}
                leftIcon={<PlusCircle color="#3E68FE" />}
                rightIcon={null}
                disabled={!isLastStudentValid}
                textProps={{}}
                textStyle={{ color: !isLastStudentValid ? "#9CA3AF" : "#3E68FE" }}
              />
            </ScrollView>
          ) : (
            <View style={styles.uploadContainer}>
              <CustomText
                text="Upload student roster using an Excel file (.xlsx)"
                size="s"
                useTranslationText={false}
                style={styles.uploadDescription}
              />
              <CustomSpacing type="vertical" size="m" />
              <View style={styles.templateSection}>
                <CustomText text="Need a template?" size="s" weight="semiBold" />
                <CustomButton
                  text="Download Template"
                  onPress={onDownloadTemplate}
                  styleType="secondary"
                  size="s"
                  style={styles.downloadButton}
                />
              </View>
              <View style={styles.fileUpload}>
                <Cloud />
                <CustomSpacing type="vertical" size="s" />
                <CustomText text="Drag & drop or browse" size="m" style={{ color: "#344054" }} />
                <CustomText text="Excel files only (.xlsx, .xls)" size="xs" style={{ color: "#5177FE" }} />
                <CustomSpacing type="vertical" size="m" />
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={onFileChange}
                  style={{ display: "none" }}
                  ref={fileInputRef}
                />
                <CustomButton
                  text="Browse Files"
                  onPress={() => fileInputRef.current?.click()}
                  styleType="primary"
                  size="m"
                />
              </View>
            </View>
          )}

          {activeTab === "manual" && isSubmitDisabled && students.length > 1 && (
            <View style={styles.validationMessageContainer}>
              <CustomText text={getValidationMessage()} size="xs" style={styles.validationMessage} />
            </View>
          )}

          <View style={styles.buttonContainer}>
            <CustomButton
              text="Cancel"
              onPress={onClose}
              styleType="secondary"
              size="m"
              style={styles.cancelButton}
              textStyle={styles.cancelButtonText}
              leftIcon={null}
              rightIcon={null}
              disabled={false}
              textProps={{}}
            />
            <CustomButton
              text="Send Invites"
              onPress={handleSubmit(handleFormSubmit)}
              styleType="primary"
              size="m"
              style={isSubmitDisabled || isSubmitting ? { backgroundColor: "#ccc" } : styles.sendButton}
              textStyle={styles.sendButtonText}
              leftIcon={null}
              rightIcon={null}
              disabled={isSubmitDisabled || isSubmitting}
              textProps={{}}
            />
          </View>
        </View>
      </CustomAdminModal>
      <NewCustomAxioActionModal
        visible={modalState === InviteStudentModalState.INVITE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.inviteConfirmation}
      />
      <NewCustomAxioActionModal
        visible={modalState === InviteStudentModalState.INVITE_ERROR}
        onClose={handleModalClose}
        modalTexts={modals.inviteError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
  },
  tabSwitcher: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: 16,
  },
  tabButton: {
    paddingVertical: 8,
    paddingRight: 20,
    borderBottomWidth: 2,
    borderColor: "transparent",
  },
  activeTabButton: {
    borderColor: "#3E68FE",
  },
  activeTabText: {
    color: "#3E68FE",
    fontWeight: "bold",
  },
  inactiveTabText: {
    color: "#6B7280",
  },
  uploadContainer: {
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginBottom: 24,
  },
  uploadDescription: {
    textAlign: "center",
    color: "#6B7280",
  },
  studentRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
  },
  formGroup: {
    flex: 1,
  },
  label: {
    color: "#111827",
    marginBottom: 8,
    fontWeight: "500",
  },
  input: {
    width: "100%",
  },
  removeButton: {
    backgroundColor: "transparent",
    padding: 0,
    marginTop: 32,
  },
  addButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
    marginBottom: 24,
    marginTop: 8,
    alignSelf: "flex-start",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  sendButton: {
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  sendButtonText: {
    color: "#FFFFFF",
  },
  validationMessageContainer: {
    marginBottom: 12,
  },
  validationMessage: {
    color: "#DC2626",
    fontSize: 14,
  },
  templateSection: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  downloadButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
  },
  fileUpload: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#3E68FE",
    borderRadius: 8,
    borderStyle: "dashed",
    padding: 24,
    alignItems: "center",
    backgroundColor: "#F2F5FF",
  },
});

export default InviteStudentModalContainer;
